import React from 'react';
import { useNavigate } from 'react-router-dom';
import { WidthProvider, Responsive } from 'react-grid-layout';
import { Card, CardContent, CardActions, Button, Typography, Box, useTheme, useMediaQuery } from '@mui/material';
import DashSummaryHandler from '../handlers/dashSummary-handler';
import PowerSumGraph from '../charts-graphs/powerSumGraph';
import { useAtomValue } from 'jotai';
import { dashSummaryAtom } from '../atoms-types/dashSummary';

const ResponsiveGridLayout = WidthProvider(Responsive);

const DashboardGrid: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery('(max-width:900px)');
  const dashSummary = useAtomValue(dashSummaryAtom);

  return (
    <Box 
      sx={{ 
        position: 'relative',
        height: '100vh',
        width: '100%',
        overflowY: isSmallScreen ? 'auto' : 'visible',
        overflowX: 'hidden',
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#888',
          borderRadius: '4px',
          '&:hover': {
            background: '#555',
          },
        },
      }}
    >
      <Box sx={{ minHeight: '100%', width: '100%' }}>
        <ResponsiveGridLayout 
          className="layout" 
          layouts={{
            lg: [
              { i: 'a', x: 0, y: 0, w: 9, h: 4, minW: 2, minH: 4 },
              { i: 'b', x: 0, y: 3, w: 9, h: 4, minW: 2, minH: 4 },
              { i: 'c', x: 6, y: 0, w: 3, h: 4, minW: 2, minH: 4 },
            ],
            sm: [
              { i: 'a', x: 0, y: 0, w: 3, h: 4, minW: 2, minH: 4 },
              { i: 'b', x: 0, y: 4, w: 3, h: 4, minW: 2, minH: 4 },
            ],
            xs: [
              { i: 'a', x: 0, y: 0, w: 1, h: 8, minW: 1, minH: 8 },
              { i: 'b', x: 0, y: 8, w: 1, h: 5, minW: 1, minH: 4 },
            ]
          }} 
          breakpoints={{ lg: 1300, md: 1200, sm: 996, xs: 768 }} 
          cols={{ lg: 9, md: 6, sm: 3, xs: 1 }}
          rowHeight={100}
          draggableCancel=".cancelSelectorName"
          isDraggable={false}
          isResizable={false}
        >
          <div key="a" style={{ minHeight: "300px" }} className='card-gauge'>
            <Card sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              width: '100%', 
              height: '100%', 
              justifyContent: 'center',
              overflow: 'visible'
            }}>
              <CardContent sx={{ 
                flexGrow: 1, 
                display: 'flex', 
                flexDirection: 'column', 
                justifyContent: 'space-between',
                padding: '16px',
                '&:last-child': {
                  paddingBottom: '16px'
                }
              }}>
                <Box sx={{ 
                  textAlign: 'center', 
                  width: '100%', 
                  height: '100%',
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  mt: '10px',
                  flex: 1,
                  minHeight: '250px'
                }}>
                  <DashSummaryHandler />
                </Box>
                <br />
                <Typography variant="subtitle1" component="div" sx={{ color: 'text.secondary', textAlign: 'center', mt: '5px' }}>
                  Environment Summary
                </Typography>
              </CardContent>
              <CardActions>
                <Button size="small" onClick={() => navigate('/environment/weather')}>Go to Environment</Button>
              </CardActions>
            </Card>
          </div>
          <div key="b" style={{ minHeight: "300px" }} className='card-gauge'>
            <Card sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              width: '100%', 
              height: '100%', 
              justifyContent: 'center',
              overflow: 'visible'
            }}>
              <CardContent sx={{ 
                flexGrow: 1, 
                display: 'flex', 
                flexDirection: 'column', 
                justifyContent: 'space-between',
                padding: '16px',
                '&:last-child': {
                  paddingBottom: '16px'
                }
              }}>
                <Box sx={{ 
                  textAlign: 'center', 
                  width: '100%', 
                  height: '100%',
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  mt: '10px',
                  flex: 1,
                  minHeight: '250px'
                }}>
                  {isSmallScreen ? (
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      gap: 2,
                      width: '100%',
                      maxWidth: '400px'
                    }}>
                      <Box sx={{ 
                        bgcolor: 'background.paper', 
                        p: 2, 
                        borderRadius: 1,
                        boxShadow: 1,
                        textAlign: 'center'
                      }}>
                        <Typography variant="h6" gutterBottom color="warning.main">Load / Solar</Typography>
                        <Typography variant="body1">Load: {dashSummary.powerSum.totalLoadKwh.toFixed(2)} kWh</Typography>
                        <Typography variant="body1">Solar: {dashSummary.powerSum.totalSolarKwh.toFixed(2)} kWh</Typography>
                      </Box>
                      <Box sx={{ 
                        bgcolor: 'background.paper', 
                        p: 2, 
                        borderRadius: 1,
                        boxShadow: 1,
                        textAlign: 'center'
                      }}>
                        <Typography variant="h6" gutterBottom color="info.main">Current Power</Typography>
                        <Typography variant="body1">Load: {dashSummary.powerSum.currentLoadPower.toFixed(2)} kW</Typography>
                        <Typography variant="body1">Solar: {dashSummary.powerSum.currentSolarPower.toFixed(2)} kW</Typography>
                      </Box>
                      <Box sx={{ 
                        bgcolor: 'background.paper', 
                        p: 2, 
                        borderRadius: 1,
                        boxShadow: 1,
                        textAlign: 'center'
                      }}>
                        <Typography variant="h6" gutterBottom color="success.main">Current Peak</Typography>
                        <Typography variant="body1">Kw: {dashSummary.powerSum.DayPeak.toFixed(3)} kW</Typography>
                        <Typography variant="body1">Time: {dashSummary.powerSum.PeakTime}</Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'row',
                      gap: { xs: 1, sm: 2, md: 3 },
                      width: '100%',
                      height: '100%',
                      justifyContent: 'space-between'
                    }}>
                      <Box sx={{ 
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        height: 'auto',
                        minWidth: 0
                      }}>
                        <PowerSumGraph type="solarLoad" />
                      </Box>
                      <Box sx={{ 
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        height: 'auto',
                        minWidth: 0
                      }}>
                        <PowerSumGraph type="currentPower" />
                      </Box>
                      <Box sx={{ 
                        bgcolor: 'background.paper', 
                        p: 2, 
                        borderRadius: 1,
                        boxShadow: 1,
                        width: '200px',
                        height: 'fit-content',
                        mt: 2,
                        textAlign: 'center',
                        flexShrink: 0
                      }}>
                        <Typography variant="h6" gutterBottom color="success.main">Current Peak</Typography>
                        <Typography variant="body1">Kw: {dashSummary.powerSum.DayPeak.toFixed(3)} kW</Typography>
                        <Typography variant="body1">Time: {dashSummary.powerSum.PeakTime}</Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
                <br />
                <Typography variant="subtitle1" component="div" sx={{ color: 'text.secondary', textAlign: 'center', mt: '5px' }}>
                  Power Summary
                </Typography>
              </CardContent>
              <CardActions>
                <Button size="small" onClick={() => navigate('/power/pool')}>Go to Power</Button>
              </CardActions>
            </Card>
          </div>
        </ResponsiveGridLayout>
      </Box>
    </Box>
  );
};

export default DashboardGrid;